import { __ConvertBoxCoinsListRef, _ConvertBoxCoinsListProps } from './coinsList.interface';
import { CiSearch } from 'react-icons/ci';
import React, { useEffect, useRef, useState } from 'react';
import CoinIcon from 'utilities/components/coinIcon/coinIcon.index';
import { GetAllCoinType, Pagination } from 'interfaces';
import { API } from 'data/30pay.api';
import { get } from 'scripts';
import { Divider } from 'antd';

const ConvertBoxCoinsList: React.ForwardRefRenderFunction<__ConvertBoxCoinsListRef, _ConvertBoxCoinsListProps> = (
    props: _ConvertBoxCoinsListProps,
    ref,
) => {
    const PAGE_SIZE = 12;
    const hardcodedPinnedCoins: GetAllCoinType[] = [
        { symbol: 'USDT', name: 'تتر' } as GetAllCoinType,
        { symbol: 'TRX', name: 'ترون' } as GetAllCoinType,
        { symbol: 'UUSD', name: 'یو ووچر' } as GetAllCoinType,
    ];
    const pinnedSymbols = ['USDT', 'TRX', 'UUSD'];
    const [coins, setCoins] = useState<GetAllCoinType[] | undefined>(undefined);
    const userSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true); // Track if there's a next page
    const containerRef = useRef<HTMLDivElement | null>(null);

    function reset() {
        setSearchTerm('');
        setPage(1);
        fetchCoins(1);
    }

    React.useImperativeHandle(ref, () => ({
        reset: () => reset(),
    }));

    useEffect(() => {
        fetchCoins(page);
    }, [page]);

    useEffect(() => {
        const handleScroll = () => {
            if (containerRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
                if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isLoading) {
                    // Check for next page
                    setPage((prevPage) => prevPage + 1);
                }
            }
        };

        const currentContainer = containerRef.current;
        currentContainer?.addEventListener('scroll', handleScroll);

        return () => {
            currentContainer?.removeEventListener('scroll', handleScroll);
        };
    }, [isLoading, hasNextPage]); // Depend on isLoading and hasNextPage

    const searchHandler = (value: string) => {
        setSearchTerm(value);
        if (userSearchTimeoutRef.current) {
            clearTimeout(userSearchTimeoutRef.current);
        }
        userSearchTimeoutRef.current = setTimeout(() => {
            setPage(1); // Reset to first page on new search
            fetchCoins(1, value);
        }, 500);
    };

    const fetchCoins = (page: number, searchTerm?: string) => {
        setCoins((prevCoins) => (page === 1 ? undefined : prevCoins)); // Clear coins on new search
        setIsLoading(true); // Set loading state to true
        const filter = searchTerm ? { Keywords: searchTerm } : {};

        get<Pagination<GetAllCoinType>>(API.market.all(page, PAGE_SIZE), filter)
            .then((response) => {
                const _response = response.value.value;
                setCoins((prevCoins) => {
                    if (page === 1) {
                        setHasNextPage(_response.pagination.hasNextPage); // Update hasNextPage on first page
                        return _response.items; // Reset on first page
                    }
                    setHasNextPage(_response.pagination.hasNextPage); // Update hasNextPage on subsequent pages
                    return prevCoins ? [...prevCoins, ..._response.items] : _response.items; // Append new coins
                });
            })
            .catch(() => setCoins([]))
            .finally(() => setIsLoading(false)); // Reset loading state
    };

    const filteredCoins = coins
        ?.filter((item) => item.symbol !== props.selectedCoin && !props.forbiddenCoins?.includes(item.symbol))
        ?.filter((coin) => !pinnedSymbols.includes(coin.symbol?.toUpperCase()));

    return (
        <div className="flex flex-col gap-8">
            <div
                className="border border-[#D2DEFE] bg-white max-w-[75%] w-full h-12 rounded-3xl p-4 flex items-center gap-2"
                style={{ direction: 'rtl' }}
            >
                <CiSearch size={16} className="text-black-40" />
                <input
                    placeholder="ارز مورد نظر خود را جستجو کنید"
                    className="font-medium w-full text-sm outline-none border-none text-darkPurple-100 placeholder:font-medium placeholder:text-sm placeholder:text-darkPurple-40"
                    onChange={(e) => searchHandler(e.target.value)}
                    value={searchTerm}
                />
            </div>
            <div
                ref={containerRef}
                className="max-h-96 overflow-y-auto overflow-x-hidden w-full flex flex-col gap-3 pr-3 -mr-3 items-end"
                style={{ direction: 'ltr' }}
            >
                {coins !== undefined ? (
                    <>
                        {/* Always render hardcoded pinned coins first */}
                        {hardcodedPinnedCoins.map((coin, index) => (
                            <div
                                key={`hardcoded-pinned-${index}`}
                                onClick={() => props.onSelect(coin)}
                                className="flex w-full items-center gap-2 p-2 cursor-pointer transition-opacity hover:bg-[#F1F8FF80] rounded-lg"
                                style={{ direction: 'rtl' }}
                            >
                                <CoinIcon symbol={coin?.symbol} className={'w-8 h-8'} />
                                <h6>{coin.name}</h6>
                            </div>
                        ))}

                        <Divider className="my-0" />

                        {/* Then render the fetched filtered coins */}
                        {filteredCoins && filteredCoins.length > 0 ? (
                            filteredCoins.map((coin, index) => (
                                <div
                                    key={index}
                                    onClick={() => props.onSelect(coin)}
                                    className="flex w-full items-center gap-2 p-2 cursor-pointer transition-opacity hover:bg-[#F1F8FF80] rounded-lg"
                                    style={{ direction: 'rtl' }}
                                >
                                    <CoinIcon symbol={coin?.symbol} className={'w-8 h-8'} />
                                    <h6>{coin.name}</h6>
                                </div>
                            ))
                        ) : (
                            <p className="text-base font-medium mx-auto my-8 text-darkPurple-60">
                                ارز مورد نظر موجود نمی‌باشد
                            </p>
                        )}

                        {/* Loading skeletons */}
                        {isLoading &&
                            Array.from({ length: 3 }).map((_, index) => (
                                <div
                                    key={`loading-${index}`}
                                    className="siPay-skeleton w-full min-h-10 max-h-10 h-full rounded-lg mb-2"
                                ></div>
                            ))}
                    </>
                ) : (
                    Array.from({ length: 6 }).map((_, index) => (
                        <div
                            key={`skeleton-${index}`}
                            className="siPay-skeleton w-full min-h-10 max-h-10 h-full rounded-lg mb-2"
                        ></div>
                    ))
                )}
            </div>
        </div>
    );
};

export default React.forwardRef(ConvertBoxCoinsList);
